import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { RootState } from '../store/store';
import { UserRole, Provider, Transfer, PaymentMode } from '../types';
import { providersService } from '../services/providersService';
import { transfersService } from '../services/transfersService';
import {
  providerPaymentsService,
  ProviderPaymentSummary,
  ProviderPayment,
  TransferPaymentDetails,
} from '../services/providerPaymentsService';

export const AdminProviderPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [summary, setSummary] = useState<ProviderPaymentSummary[]>([]);
  const [payments, setPayments] = useState<ProviderPayment[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>(format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(endOfMonth(new Date()), 'yyyy-MM-dd'));

  // Modals
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isPaymentHistoryModalOpen, setIsPaymentHistoryModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [transferDetails, setTransferDetails] = useState<TransferPaymentDetails | null>(null);

  // Payment form
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentDate, setPaymentDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [paymentNotes, setPaymentNotes] = useState<string>('');

  // Transfer selection for payment
  const [providerTransfers, setProviderTransfers] = useState<Transfer[]>([]);
  const [selectedTransferForPayment, setSelectedTransferForPayment] = useState<string>('');
  const [selectedProviderPaymentMode, setSelectedProviderPaymentMode] = useState<PaymentMode>(PaymentMode.PER_TRANSFER);

  // Check access
  const canViewPayments = user?.role === UserRole.MANAGER ||
                          user?.role === UserRole.ORG_ADMIN ||
                          user?.role === UserRole.PLATFORM_ADMIN;

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (providers.length > 0) {
      loadData();
    }
  }, [selectedProviderId, startDate, endDate, providers]);

  const loadProviders = async () => {
    try {
      const data = await providersService.getAll(true);
      setProviders(data);
    } catch (error) {
      toast.error(t('payments.failedToLoadProviders'));
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {
        providerId: selectedProviderId || undefined,
        startDate,
        endDate,
      };

      const [summaryData, paymentsData] = await Promise.all([
        providerPaymentsService.getSummary(filters),
        providerPaymentsService.getAll(filters),
      ]);

      setSummary(summaryData);
      setPayments(paymentsData);
    } catch (error: any) {
      toast.error(t('payments.failedToLoadData'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAddPaymentModal = async (providerId: string) => {
    try {
      // Find the provider to check payment mode
      const provider = providers.find(p => p.id === providerId);
      const paymentMode = provider?.paymentMode || PaymentMode.PER_TRANSFER;
      setSelectedProviderPaymentMode(paymentMode);
      setSelectedProviderId(providerId);

      // Only load transfers for PER_TRANSFER mode
      if (paymentMode === PaymentMode.PER_TRANSFER) {
        const unpaidTransfers = await providerPaymentsService.getUnpaidTransfers(providerId);
        setProviderTransfers(unpaidTransfers as any);
      } else {
        setProviderTransfers([]);
      }

      setIsAddPaymentModalOpen(true);
    } catch (error) {
      toast.error(t('payments.failedToLoadTransfers'));
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only require transfer for PER_TRANSFER mode
    if (selectedProviderPaymentMode === PaymentMode.PER_TRANSFER && !selectedTransferForPayment) {
      toast.error(t('payments.selectTransferRequired'));
      return;
    }

    if (!paymentAmount || Number(paymentAmount) <= 0) {
      toast.error(t('payments.validAmountRequired'));
      return;
    }

    try {
      await providerPaymentsService.create({
        transferId: selectedProviderPaymentMode === PaymentMode.PER_TRANSFER ? selectedTransferForPayment : undefined,
        providerId: selectedProviderId,
        amount: Number(paymentAmount),
        paymentDate,
        paymentMethod,
        notes: paymentNotes || undefined,
      });

      toast.success(t('payments.paymentRecorded'));
      setIsAddPaymentModalOpen(false);
      resetPaymentForm();
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('payments.failedToRecord'));
    }
  };

  const resetPaymentForm = () => {
    setPaymentAmount('');
    setPaymentDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMethod('cash');
    setPaymentNotes('');
    setSelectedTransferForPayment('');
    setProviderTransfers([]);
  };

  const openPaymentHistory = async (transferId: string) => {
    try {
      const details = await providerPaymentsService.getTransferDetails(transferId);
      setTransferDetails(details);
      setIsPaymentHistoryModalOpen(true);
    } catch (error) {
      toast.error(t('payments.failedToLoadHistory'));
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm(t('payments.deletePaymentConfirm'))) {
      return;
    }

    try {
      await providerPaymentsService.delete(paymentId);
      toast.success(t('payments.paymentDeleted'));
      loadData();
      if (transferDetails) {
        const updated = await providerPaymentsService.getTransferDetails(transferDetails.transfer.id);
        setTransferDetails(updated);
      }
    } catch (error) {
      toast.error(t('payments.failedToDelete'));
    }
  };

  if (!canViewPayments) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('payments.accessDenied')}</h2>
          <p className="text-gray-600">{t('payments.managersOnly')}</p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totalAmount = summary.reduce((sum, p) => sum + Number(p.totalAmount), 0);
  const totalPaid = summary.reduce((sum, p) => sum + Number(p.totalPaid), 0);
  const totalPending = summary.reduce((sum, p) => sum + Number(p.totalPending), 0);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('payments.providerPayments')}</h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">{t('payments.providerPaymentsSubtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('payments.filters')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('payments.provider')}
            </label>
            <select
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            >
              <option value="">{t('payments.allProviders')}</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('payments.startDate')}
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
              {t('payments.endDate')}
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-blue-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-blue-600">{t('payments.totalAmount')}</p>
              <p className="text-xl sm:text-3xl font-bold text-blue-900">{totalAmount.toFixed(2)} TL</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-green-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-green-600">{t('payments.totalPaid')}</p>
              <p className="text-xl sm:text-3xl font-bold text-green-900">{totalPaid.toFixed(2)} TL</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow-lg p-4 sm:p-6 border-2 border-orange-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-8 w-8 sm:h-12 sm:w-12 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-orange-600">{t('payments.totalPending')}</p>
              <p className="text-xl sm:text-3xl font-bold text-orange-900">{totalPending.toFixed(2)} TL</p>
            </div>
          </div>
        </div>
      </div>

      {/* Provider Summary Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">{t('payments.providerSummary')}</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">{t('payments.loading')}</div>
        ) : summary.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('payments.noDataFound')}</div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {summary.map((item) => (
                <div key={item.providerId} className="p-4">
                  {/* Provider Name & Status */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.providerName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          item.paymentMode === PaymentMode.LUMP_SUM
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.paymentMode === PaymentMode.LUMP_SUM ? 'Cari' : 'Transfer'}
                        </span>
                        {Number(item.totalPending) > 0 ? (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            {t('payments.hasPending')}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {t('payments.fullyPaid')}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => openAddPaymentModal(item.providerId)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[36px]"
                    >
                      {t('payments.addPayment')}
                    </button>
                  </div>

                  {/* Balance for Cari mode */}
                  {item.paymentMode === PaymentMode.LUMP_SUM && item.balance !== undefined && (
                    <div className="text-sm text-blue-600 mb-2">
                      Bakiye: {Number(item.balance).toFixed(2)} TL
                    </div>
                  )}

                  {/* Transfer Counts */}
                  <div className="text-sm text-gray-600 mb-3">
                    {item.transferCount} {t('payments.transfers')} • {item.paidTransferCount} {t('payments.paid').toLowerCase()} • {item.pendingTransferCount} {t('payments.pending').toLowerCase()}
                  </div>

                  {/* Financial Grid */}
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-gray-50 rounded-lg p-2">
                      <p className="text-xs text-gray-500">{t('payments.total')}</p>
                      <p className="font-bold text-gray-900 text-sm">{Number(item.totalAmount).toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2">
                      <p className="text-xs text-green-600">{t('payments.paid')}</p>
                      <p className="font-bold text-green-700 text-sm">{Number(item.totalPaid).toFixed(2)}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-2">
                      <p className="text-xs text-orange-600">{t('payments.pending')}</p>
                      <p className="font-bold text-orange-700 text-sm">{Number(item.totalPending).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.provider')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.mode')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.transfers')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.totalAmount')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.paid')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.pending')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {summary.map((item) => (
                    <tr key={item.providerId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {item.providerName}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          item.paymentMode === PaymentMode.LUMP_SUM
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {item.paymentMode === PaymentMode.LUMP_SUM ? 'Cari' : 'Transfer'}
                        </span>
                        {item.paymentMode === PaymentMode.LUMP_SUM && item.balance !== undefined && (
                          <div className="text-xs mt-1 text-blue-600">
                            Bakiye: {Number(item.balance).toFixed(2)} TL
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div>{item.transferCount} {t('payments.total').toLowerCase()}</div>
                        <div className="text-xs text-gray-500">
                          {item.paidTransferCount} {t('payments.paid').toLowerCase()} • {item.pendingTransferCount} {t('payments.pending').toLowerCase()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900">
                        {Number(item.totalAmount).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        {Number(item.totalPaid).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-orange-600">
                        {Number(item.totalPending).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {Number(item.totalPending) > 0 ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            {t('payments.hasPending')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {t('payments.fullyPaid')}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => openAddPaymentModal(item.providerId)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          {t('payments.addPayment')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{t('payments.recentPayments')}</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-600">{t('payments.loading')}</div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('payments.noPaymentsYet')}</div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-gray-200">
              {payments.map((payment) => (
                <div key={payment.id} className="p-4">
                  {/* Header: Provider & Amount */}
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{payment.provider?.name || 'N/A'}</p>
                      <p className="text-sm text-gray-500">{format(new Date(payment.paymentDate), 'dd.MM.yyyy')}</p>
                    </div>
                    <p className="font-bold text-green-600 text-lg">{Number(payment.amount).toFixed(2)} TL</p>
                  </div>

                  {/* Transfer Info */}
                  <div className="mb-3">
                    {payment.transfer ? (
                      <div className="text-sm">
                        <span className="text-gray-900">#{payment.transfer.transferCode}</span>
                        <span className="text-gray-500"> • {payment.transfer.pickupLocation} → {payment.transfer.dropoffLocation}</span>
                      </div>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        Toplu Ödeme
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex items-center justify-between text-sm">
                    <div className="text-gray-500">
                      <span className="capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                      {payment.recordedBy?.fullName && (
                        <span> • {payment.recordedBy.fullName}</span>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {payment.transferId && (
                        <button
                          onClick={() => openPaymentHistory(payment.transferId!)}
                          className="text-blue-600 hover:text-blue-900 font-medium min-h-[36px] px-2"
                        >
                          {t('payments.view')}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePayment(payment.id)}
                        className="text-red-600 hover:text-red-900 font-medium min-h-[36px] px-2"
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.provider')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.transfer')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.amount')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.paymentMethod')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.recordedBy')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('payments.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {format(new Date(payment.paymentDate), 'dd.MM.yyyy')}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {payment.provider?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {payment.transfer ? (
                          <>
                            <div>#{payment.transfer.transferCode}</div>
                            <div className="text-xs text-gray-500">
                              {payment.transfer.pickupLocation} → {payment.transfer.dropoffLocation}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            Toplu Ödeme
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600">
                        {Number(payment.amount).toFixed(2)} TL
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                        {payment.paymentMethod.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {payment.recordedBy?.fullName || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm space-x-2">
                        {payment.transferId && (
                          <button
                            onClick={() => openPaymentHistory(payment.transferId!)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            {t('payments.view')}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Add Payment Modal */}
      {isAddPaymentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                    {selectedProviderPaymentMode === PaymentMode.LUMP_SUM ? t('payments.lumpSumPayment') : t('payments.recordPayment')}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedProviderPaymentMode === PaymentMode.LUMP_SUM
                      ? t('payments.lumpSumProviderDesc')
                      : t('payments.perTransferModeDesc')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsAddPaymentModalOpen(false);
                    resetPaymentForm();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            </div>

            <form onSubmit={handleAddPayment} className="p-4 sm:p-6 space-y-4">
              {/* Payment Mode Info */}
              <div className={`p-3 rounded-lg ${selectedProviderPaymentMode === PaymentMode.LUMP_SUM ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                <p className="text-sm font-medium">
                  {t('payments.paymentModeInfo')}: {selectedProviderPaymentMode === PaymentMode.LUMP_SUM ? (
                    <span className="text-blue-600">{t('payments.lumpSumAccount')}</span>
                  ) : (
                    <span className="text-gray-600">{t('payments.perTransferBased')}</span>
                  )}
                </p>
                {selectedProviderPaymentMode === PaymentMode.LUMP_SUM && (
                  <p className="text-xs text-blue-600 mt-1">
                    {t('payments.lumpSumProviderInfo')}
                  </p>
                )}
              </div>

              {/* Transfer Selection - Only for PER_TRANSFER mode */}
              {selectedProviderPaymentMode === PaymentMode.PER_TRANSFER && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('payments.selectTransfer')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedTransferForPayment}
                    onChange={(e) => {
                      setSelectedTransferForPayment(e.target.value);
                      const transfer = providerTransfers.find((t: any) => t.id === e.target.value) as any;
                      if (transfer) {
                        // Set payment amount to remaining balance
                        const remaining = transfer.remaining || transfer.providerPrice || 0;
                        setPaymentAmount(remaining.toString());
                      }
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">{t('payments.chooseTransfer')}</option>
                    {providerTransfers.map((transfer: any) => (
                      <option key={transfer.id} value={transfer.id}>
                        #{transfer.transferId} - {format(new Date(transfer.transferDate), 'dd.MM.yyyy')} - {' '}
                        {transfer.pickupLocation} → {transfer.dropoffLocation} - {' '}
                        {t('payments.remaining')}: {transfer.remaining?.toFixed(2) || transfer.providerPrice} TL
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('payments.paymentAmount')} (TL) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('payments.paymentDate')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('payments.paymentMethod')}
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">{t('payments.cash')}</option>
                  <option value="bank_transfer">{t('payments.bankTransfer')}</option>
                  <option value="check">{t('payments.check')}</option>
                  <option value="credit_card">{t('payments.creditCard')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('payments.notesOptional')}
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder={t('payments.additionalNotes')}
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddPaymentModalOpen(false);
                    resetPaymentForm();
                  }}
                  className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 active:bg-gray-300 min-h-[44px] font-medium"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 min-h-[44px] font-medium"
                >
                  {t('payments.recordPayment')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {isPaymentHistoryModalOpen && transferDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('payments.paymentHistory')}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Transfer #{transferDetails.transfer.transferCode || '-'} - {transferDetails.transfer.providerName}
                  </p>
                </div>
                <button
                  onClick={() => setIsPaymentHistoryModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl p-1 min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6">
              {/* Transfer Summary */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">{t('payments.date')}</p>
                    <p className="font-semibold text-sm sm:text-base">{format(new Date(transferDetails.transfer.transferDate), 'dd.MM.yyyy')}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-xs sm:text-sm text-gray-600">{t('payments.route')}</p>
                    <p className="font-semibold text-sm sm:text-base">
                      {transferDetails.transfer.pickupLocation} → {transferDetails.transfer.dropoffLocation}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">{t('payments.totalPrice')}</p>
                    <p className="font-semibold text-blue-600 text-sm sm:text-base">{transferDetails.totalPrice.toFixed(2)} TL</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">{t('payments.totalPaid')}</p>
                    <p className="font-semibold text-green-600 text-sm sm:text-base">{transferDetails.totalPaid.toFixed(2)} TL</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">{t('payments.remaining')}</p>
                    <p className="font-semibold text-orange-600 text-sm sm:text-base">{transferDetails.remaining.toFixed(2)} TL</p>
                  </div>
                </div>
              </div>

              {/* Payments List */}
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">{t('payments.paymentRecords')}</h3>
              {transferDetails.payments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('payments.noPaymentsYet')}</p>
              ) : (
                <div className="space-y-3">
                  {transferDetails.payments.map((payment) => (
                    <div key={payment.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-bold text-green-600">{Number(payment.amount).toFixed(2)} TL</span>
                            <span className="text-xs bg-gray-100 px-2 py-1 rounded capitalize">{payment.paymentMethod.replace('_', ' ')}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">
                            {format(new Date(payment.paymentDate), 'dd MMMM yyyy')}
                          </p>
                          {payment.notes && (
                            <p className="text-sm text-gray-500 mt-1 italic break-words">{payment.notes}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            {t('payments.recordedBy')}: {payment.recordedBy?.fullName} - {' '}
                            {format(new Date(payment.createdAt), 'dd.MM.yyyy HH:mm')}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 hover:text-red-900 active:text-red-950 text-sm font-medium px-2 py-1 min-h-[36px] flex-shrink-0"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end sticky bottom-0 bg-white">
              <button
                onClick={() => setIsPaymentHistoryModalOpen(false)}
                className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 active:bg-gray-300 min-h-[44px] font-medium"
              >
                {t('payments.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
